import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, BadgeCheck, Link2, Eye, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const PILLARS = [
  {
    icon: Lock,
    title: "AES-256 encryption",
    desc: "All data is encrypted at rest and in transit using AES-256 — the same standard trusted by governments and financial institutions worldwide.",
    points: ["Encrypted at rest", "Encrypted in transit (TLS 1.2+)", "Managed key rotation"],
  },
  {
    icon: BadgeCheck,
    title: "SOC-2 ready",
    desc: "Our controls, processes and infrastructure are built to meet SOC-2 requirements across security, availability and confidentiality.",
    points: ["Security controls", "Availability monitoring", "Confidentiality safeguards"],
  },
  {
    icon: Link2,
    title: "SHA-256 audit chain",
    desc: "Every governance action is written to a tamper-evident, SHA-256 hashed audit chain — so you always have a verifiable history of who did what.",
    points: ["Tamper-evident logs", "Cryptographic hashing", "Full action history"],
  },
];

const SCOPES = [
  { scope: "Read: records & metadata", why: "Required to extract and unify data from connected platforms." },
  { scope: "Read: usage & limits", why: "Required to monitor limits and trigger real-time alerts." },
  { scope: "Write: governed actions", why: "Only used when you explicitly approve a governance action." },
];

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.08, ease: "easeOut" } }),
};

export default function Security() {
  return (
    <div data-testid="security-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-lines opacity-[0.12] dark:opacity-[0.18]" />
        <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
          <div className="max-w-3xl">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Security
            </p>
            <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              Security you can <span className="text-primary">verify</span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
              Governance is only as good as the trust behind it. Pravadh GovernX is built on
              enterprise-grade encryption, tamper-evident audit trails and full OAuth transparency.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20" data-testid="security-pillars">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-5 lg:grid-cols-3">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  custom={i}
                  variants={fade}
                  className="flex flex-col rounded-2xl border border-border bg-card p-7 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
                  data-testid={`security-pillar-${i}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 shrink-0 text-primary" />
                        <span className="text-muted-foreground">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OAuth scope transparency */}
      <section className="border-t border-border bg-card/40 py-20" data-testid="oauth-transparency">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-primary">
              <Eye className="h-3.5 w-3.5" /> OAuth scope transparency
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              You always know what we access
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              We request the minimum OAuth scopes needed — and we tell you exactly why each one is used.
              Nothing more, nothing hidden.
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-border bg-background">
            {SCOPES.map((s, i) => (
              <div
                key={s.scope}
                className={`grid gap-2 p-5 sm:grid-cols-[280px_1fr] sm:gap-8 ${
                  i !== 0 ? "border-t border-border" : ""
                }`}
                data-testid={`oauth-scope-${i}`}
              >
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-muted px-2.5 py-1 font-mono text-xs font-medium text-foreground">
                    {s.scope}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{s.why}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="security-cta">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Govern with confidence
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-muted-foreground">
            Start your free trial and see enterprise-grade security in action.
          </p>
          <Link to="/signup">
            <Button size="lg" className="mt-7 h-12 gap-2 px-6 font-semibold" data-testid="security-cta-button">
              Start free trial <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
